import React, { use, useEffect, useCallback, useMemo, useRef } from "react";
import { type Creditor, type Debitor, type GenericRecord, type Tag, Category } from "@domain/models";
import { BuTaxesSKR } from "@domain/models/BuTaxUtils";
import { CompanyContext } from "@domain/context/CompanyContext";
import { BuContext } from "@domain/context/BuContext";
import { type AccountMaps, KontoContext } from "@domain/context/accountingData/KontoEntitiesProvider";

export const useEnrichAccounts = () => {
    const { companyGQL } = use(CompanyContext);
    const { companyBuTimeframes } = use(BuContext);
    const { accountMapsAllYears } = use(KontoContext);
    const accountsRef = useRef<Map<number, AccountMaps>>(accountMapsAllYears);
    useEffect(() => {
        accountsRef.current = accountMapsAllYears;
    }, [accountMapsAllYears]);
    const accountingYearConfigs = useMemo(() => companyGQL?.accountingConfigs ?? [], [companyGQL]);

    return useCallback(
        (value: GenericRecord): GenericRecord => {
            const yearConfig = accountingYearConfigs.find((v) => v.year === (value.year ?? value.date.getFullYear()));
            const accountMaps = accountsRef.current.get(yearConfig?.year);
            if (!yearConfig || !companyBuTimeframes || !accountMaps) {
                return value;
            }

            const enrichCat = (category?: Category): Category => {
                if (!category) {
                    return undefined;
                }
                const { num } = category;
                if (!num) {
                    return undefined;
                }
                const buTimeframe = BuTaxesSKR.getBuTimeframeYearPeriod(
                    yearConfig.skr,
                    companyBuTimeframes,
                    value.year,
                    value.period,
                );
                if (!buTimeframe) {
                }
                const shortNum = Number(category.num.substring(0, 4));
                const defaultCat = buTimeframe.defaultCategories.get(shortNum);
                // fallback to the default cat name in case of the race condition (save record + new cat)
                const name = accountMaps.categoryOverridesMap.get(num)?.name || defaultCat?.name || "";
                let cat = defaultCat;
                const encoded = JSON.stringify(defaultCat);
                try {
                    cat = JSON.parse(encoded);
                } catch {
                    // logger.logToSentry("enrich cat error", {
                    //     value,
                    //     num,
                    //     shortNum,
                    //     defaultCat,
                    //     encoded,
                    //     tf: buTimeframe.defaultCategories?.size,
                    //     ucat: accountsRef.current.categoryOverridesMap?.size,
                    // });
                }
                return Category.unserialize({ ...cat, num, name });
            };
            const enrichCred = (creditor?: Creditor): Creditor => {
                if (!creditor || !accountMaps.creditorsMap.has(creditor.num)) {
                    return creditor ?? undefined;
                }
                return accountMaps.creditorsMap.get(creditor.num);
            };
            const enrichDeb = (debitor?: Debitor): Debitor => {
                if (!debitor || !accountMaps.debitorsMap.has(debitor.num)) {
                    return debitor ?? undefined;
                }
                return accountMaps.debitorsMap.get(debitor.num);
            };
            const enrichTag = (tag?: Tag): Tag => {
                if (!tag || !accountMaps.tagsMap.has(tag.num)) {
                    return tag ?? undefined;
                }
                return accountMaps.tagsMap.get(tag.num);
            };

            value.category = enrichCat(value.category);
            value.creditor = enrichCred(value.creditor);
            value.debetor = enrichDeb(value.debetor);
            value.items.forEach((item) => (item.category = enrichCat(item.category)));
            value.items.forEach((item) => (item.creditor = enrichCred(item.creditor)));
            value.items.forEach((item) => (item.debetor = enrichDeb(item.debetor)));
            value.items.forEach((item) => (item.tag = enrichTag(item.tag)));
            return value;
        },
        [companyBuTimeframes, accountingYearConfigs, accountsRef],
    );
};
